import { z } from 'zod';
import { checkoutStateSchema } from './checkout';
import { isoDateSchema } from './day';
import { REFUND_TIERS } from './refund';
import { reservationStatusSchema } from './reservation';
import { cancellationPolicySchema } from './settings';

// A booking is its code and the address it was made with, and nothing else. §12
export const bookingLookupSchema = z.object({
  code: z
    .string()
    .trim()
    .min(3)
    .max(40)
    .transform((code) => code.toUpperCase()),
  email: z.email().max(160),
});

export type BookingLookup = z.infer<typeof bookingLookupSchema>;

export const guestSpotSchema = z.object({
  number: z.number().int().positive(),
  zoneKey: z.string(),
  zoneName: z.string(),
});

export type GuestSpot = z.infer<typeof guestSpotSchema>;

export const holdChoiceSchema = z.object({
  minutes: z.number().int().nonnegative(),
  feeCents: z.number().int(),
});

export const refundQuoteSchema = z.object({
  tier: z.enum(REFUND_TIERS),
  pct: z.number().int().min(0).max(100),
  paidCents: z.number().int().nonnegative(),
  grossCents: z.number().int().nonnegative(),
  feeCents: z.number().int().nonnegative(),
  refundCents: z.number().int().nonnegative(),
});

// What the guest's own screen shows: their booking, what it would cost to
// extend, and what cancelling would give back today. §12, §13
export const guestBookingSchema = z.object({
  code: z.string(),
  guestName: z.string(),
  venueName: z.string(),
  spotKind: z.string(),
  currency: z.string(),
  language: z.string(),
  startsOn: isoDateSchema,
  endsOn: isoDateSchema,
  holdMinutes: z.number().int().nonnegative(),
  paidCents: z.number().int().nonnegative(),
  status: reservationStatusSchema,
  seated: z.boolean(),
  spots: z.array(guestSpotSchema),
  extensions: z.array(holdChoiceSchema),
  refund: refundQuoteSchema,
  cancellation: cancellationPolicySchema,
});

export type GuestBooking = z.infer<typeof guestBookingSchema>;

// Always charged online, whatever the venue takes at its counter. §12, §13
export const guestExtensionSchema = bookingLookupSchema.extend({
  holdMinutes: z
    .number()
    .int()
    .min(0)
    .max(24 * 60),
});

export type GuestExtension = z.infer<typeof guestExtensionSchema>;

export const guestExtensionStartedSchema = z.object({
  code: z.string(),
  state: checkoutStateSchema,
  amountCents: z.number().int().nonnegative(),
  currency: z.string(),
  holdMinutes: z.number().int().nonnegative(),
  redirectUrl: z.url().nullable(),
});

export type GuestExtensionStarted = z.infer<typeof guestExtensionStartedSchema>;

export const guestCancelledSchema = z.object({
  code: z.string(),
  currency: z.string(),
  refund: refundQuoteSchema,
});

export type GuestCancelled = z.infer<typeof guestCancelledSchema>;
